// Kampagne — the campaign world map.
//
// One painted realm with the nine campaign maps sitting on it as wax-seal
// stations along a winding route. The route is a spline through CAMPAIGN_NODES,
// drawn at runtime rather than baked into the art so it can animate and can
// never sit a few pixels off the painting (see
// docs/map_templates/CAMPAIGN_MAP_PROMPT.md).
//
// The route reads as progress: everything up to the furthest unlocked station
// is a travelled gold road, everything beyond is a faint dashed track into
// unknown country, and a light trail runs the segment into the station the
// player may take next.
//
// This is a full scene (entered with goTo, menu stops), unlike the Schnellspiel
// atlas which is an overlay on top of MenuScene. Both share the unlock rule
// (maps/progression) and the difficulty popup (ui/campaignUi).
import Phaser from "phaser";
import { GAME_HEIGHT, GAME_WIDTH } from "../config";
import { CAMPAIGN_MAPS, type MapDef } from "../maps/maps";
import { furthestUnlockedIndex, isMapLocked, lockRequirement } from "../maps/progression";
import { save } from "../systems/Save";
import { sound } from "../systems/Sound";
import { ensureImages } from "../assets/lazyLoad";
import { goTo } from "../ui/sceneTransition";
import { addStars, openDifficultyPopup } from "../ui/campaignUi";
import { beginSceneLifecycle, menuButton, type SceneLifecycle } from "../ui/menuUi";
import { THEME, panel } from "../ui/theme";

/**
 * Station anchors on the 1280x720 world map, in campaign order. Deliberately
 * serpentine — the brief calls for a bent number line, not a row of dots.
 *
 * This array is the source of truth for the art handoff too: the painting has
 * to put a readable landmark on each of these points. Changing one here means
 * updating the table in docs/map_templates/CAMPAIGN_MAP_PROMPT.md.
 */
export const CAMPAIGN_NODES: readonly { x: number; y: number }[] = [
  { x: 152, y: 592 },   // 1 Greenwold Pass
  { x: 326, y: 528 },   // 2 Stonebridge
  { x: 498, y: 592 },   // 3 Frostfell Gorge
  { x: 652, y: 486 },   // 4 Whispering Woods
  { x: 742, y: 366 },   // 5 Dragon's Spine
  { x: 596, y: 268 },   // 6 Sunken Marsh
  { x: 764, y: 186 },   // 7 Goldmere Coast
  { x: 948, y: 262 },   // 8 Ironforge Approach
  { x: 1112, y: 168 },  // 9 Twilight Chasm
];

const CAMPAIGN_MAP_ASSET = { key: "ui-campaign-map", url: "ui/campaign-map.webp" };

const SEAL_RADIUS = 27;
/** Samples along the spline. High enough that the dashes read as a drawn
 *  track rather than a polygon. */
const ROUTE_SAMPLES = 320;

/** Biome tints for the procedural realm drawn when the painting is absent. */
const BIOME_TINT: Record<MapDef["biome"], number> = {
  plains: 0x5f7a2c,
  river:  0x2f7c96,
  snow:   0xdfe9f2,
  swamp:  0x3d5230,
  mine:   0x3a3028,
  desert: 0xc8a868,
  siege:  0x4a3878,
};

export class CampaignScene extends Phaser.Scene {
  private lifecycle!: SceneLifecycle;
  private route!: Phaser.Curves.Spline;
  private popup?: Phaser.GameObjects.Container;
  private preview?: Phaser.GameObjects.Container;
  private trail?: Phaser.GameObjects.Arc;
  /** Seal containers by campaign index, so hover/pulse can address them. */
  private seals: Phaser.GameObjects.Container[] = [];

  constructor() { super("CampaignScene"); }

  create(): void {
    // Phaser reuses the instance across shutdown/start — every field that
    // holds a GameObject has to be dropped or it points at a destroyed one.
    this.lifecycle = beginSceneLifecycle(this);
    this.popup = undefined;
    this.preview = undefined;
    this.trail = undefined;
    this.seals = [];
    this.input.enabled = true;

    sound.attach(this);

    const data = save.load();
    this.route = new Phaser.Curves.Spline(
      CAMPAIGN_NODES.map(n => new Phaser.Math.Vector2(n.x, n.y))
    );

    this.drawRealm();
    this.drawRoute(data);
    this.drawStations(data);
    this.drawChrome(data);

    // The painting is not in the boot bundle. If it lands, redraw once so the
    // procedural realm is replaced without the player having to leave.
    if (!this.textures.exists(CAMPAIGN_MAP_ASSET.key)) {
      void ensureImages(this, [CAMPAIGN_MAP_ASSET]).then(() => {
        if (this.scene.isActive() && this.textures.exists(CAMPAIGN_MAP_ASSET.key)) this.scene.restart();
      });
    }

    this.lifecycle.key("keydown-ESC", () => {
      if (this.popup) this.closePopup();
      else this.back();
    });
  }

  /* ------------------------------------------------------------- backdrop -- */

  private drawRealm(): void {
    this.cameras.main.setBackgroundColor(THEME.hex.ironDeep);

    if (this.textures.exists(CAMPAIGN_MAP_ASSET.key)) {
      const image = this.add.image(GAME_WIDTH / 2, GAME_HEIGHT / 2, CAMPAIGN_MAP_ASSET.key).setOrigin(0.5);
      image.setScale(Math.max(GAME_WIDTH / image.width, GAME_HEIGHT / image.height));
      // A slow drift keeps the parchment from feeling like a static screenshot.
      this.tweens.add({
        targets: image,
        x: { from: GAME_WIDTH / 2 - 5, to: GAME_WIDTH / 2 + 5 },
        duration: 14_000, yoyo: true, repeat: -1, ease: "Sine.easeInOut",
      });
    } else {
      this.drawProceduralRealm();
    }

    // Vignette + top/bottom scrims so the title and buttons stay readable over
    // whatever the painting does in those bands.
    const scrim = this.add.graphics();
    scrim.fillStyle(THEME.hex.ironDeep, 0.55);
    scrim.fillRect(0, 0, GAME_WIDTH, 104);
    scrim.fillRect(0, GAME_HEIGHT - 60, GAME_WIDTH, 60);
    scrim.lineStyle(2, THEME.hex.goldDim, 0.5);
    scrim.lineBetween(28, 104, GAME_WIDTH - 28, 104);
    scrim.lineBetween(28, GAME_HEIGHT - 60, GAME_WIDTH - 28, GAME_HEIGHT - 60);
  }

  /** Stand-in realm for a build without the painted map: parchment with a soft
   *  regional wash and hand-drawn atlas marks around every station, so the
   *  screen reads as an illustrated map rather than a debug diagram. Replaced
   *  wholesale the moment public/ui/campaign-map.webp is delivered. */
  private drawProceduralRealm(): void {
    // Deterministic, so the realm looks the same on every visit instead of
    // reshuffling under the player.
    const rand = Phaser.Math.RND;
    rand.sow(["mittelalte-campaign"]);

    const g = this.add.graphics();
    g.fillStyle(0xd9c396, 1);
    g.fillRect(0, 0, GAME_WIDTH, GAME_HEIGHT);

    // Parchment grain: many small specks rather than a few big discs, which
    // read as blur/bokeh instead of paper.
    for (let i = 0; i < 1400; i++) {
      g.fillStyle(rand.pick([0xe8d7ae, 0xc4ac7e, 0xdcc79a, 0xcbb083]), rand.realInRange(0.05, 0.16));
      g.fillCircle(rand.between(0, GAME_WIDTH), rand.between(0, GAME_HEIGHT), rand.realInRange(2, 9));
    }

    // Regional wash + terrain marks per station. The wash stays weak: on a
    // world map the terrain should tint the parchment, not paint over it.
    CAMPAIGN_NODES.forEach((node, i) => {
      const map = CAMPAIGN_MAPS[i];
      if (!map) return;
      const tint = BIOME_TINT[map.biome];

      for (let ring = 5; ring >= 1; ring--) {
        g.fillStyle(tint, 0.028 * ring);
        g.fillCircle(node.x, node.y, 34 * ring);
      }
      this.drawTerrainMarks(g, node, map.biome, rand);
    });

    // Ink coastline along the bottom-right so the parchment has a horizon,
    // plus hatching on the seaward side.
    g.lineStyle(2.5, 0x3a2a18, 0.42);
    const coast: [number, number][] = [[1280, 452], [1136, 498], [1020, 546], [916, 614], [812, 664], [742, 720]];
    g.beginPath();
    g.moveTo(coast[0][0], coast[0][1]);
    for (const [x, y] of coast.slice(1)) g.lineTo(x, y);
    g.strokePath();
    for (let i = 0; i < coast.length - 1; i++) {
      for (let t = 0; t < 1; t += 0.12) {
        const x = Phaser.Math.Linear(coast[i][0], coast[i + 1][0], t);
        const y = Phaser.Math.Linear(coast[i][1], coast[i + 1][1], t);
        g.lineStyle(1.5, 0x2f7c96, 0.3);
        g.lineBetween(x + 8, y + 8, x + 20, y + 20);
      }
    }

    // Edge vignette keeps the eye on the route.
    const vignette = this.add.graphics();
    for (let i = 0; i < 40; i++) {
      vignette.lineStyle(2, 0x6b5228, 0.03);
      vignette.strokeRect(i, i, GAME_WIDTH - i * 2, GAME_HEIGHT - i * 2);
    }
  }

  /** Hand-drawn atlas symbols scattered in a ring around a station: tree tufts
   *  for woodland, wave dashes for water, chevrons for rock and ice. The inner
   *  radius is left clear for the seal and its name plate. */
  private drawTerrainMarks(
    g: Phaser.GameObjects.Graphics,
    node: { x: number; y: number },
    biome: MapDef["biome"],
    rand: typeof Phaser.Math.RND
  ): void {
    const ink = 0x3a2a18;
    for (let i = 0; i < 22; i++) {
      const angle = rand.realInRange(0, Math.PI * 2);
      const radius = rand.realInRange(74, 152);
      const x = node.x + Math.cos(angle) * radius;
      const y = node.y + Math.sin(angle) * radius * 0.72;
      if (x < 24 || x > GAME_WIDTH - 24 || y < 116 || y > GAME_HEIGHT - 70) continue;

      switch (biome) {
        case "plains":
        case "swamp": {
          // Tree tuft: a canopy blob over a short trunk.
          const s = rand.realInRange(4, 7);
          g.fillStyle(biome === "swamp" ? 0x3d5230 : 0x4f6a26, 0.5);
          g.fillCircle(x, y - s * 0.5, s);
          g.lineStyle(1.5, ink, 0.45);
          g.lineBetween(x, y + s * 0.4, x, y + s * 1.3);
          break;
        }
        case "river": {
          // Wave dashes.
          const w = rand.realInRange(7, 13);
          g.lineStyle(1.6, 0x2f7c96, 0.5);
          g.beginPath();
          g.moveTo(x - w, y);
          g.lineTo(x - w / 3, y - 3);
          g.lineTo(x + w / 3, y);
          g.lineTo(x + w, y - 3);
          g.strokePath();
          break;
        }
        default: {
          // Ridge chevron — rock, ice, forge highlands and the chasm all read
          // as broken ground on an atlas.
          const s = rand.realInRange(5, 10);
          const tint = biome === "snow" ? 0x8fa8bd : biome === "siege" ? 0x4a3878 : 0x4a3a2c;
          g.lineStyle(1.8, tint, 0.55);
          g.beginPath();
          g.moveTo(x - s, y + s * 0.6);
          g.lineTo(x, y - s * 0.6);
          g.lineTo(x + s, y + s * 0.6);
          g.strokePath();
          break;
        }
      }
    }
  }

  /* ---------------------------------------------------------------- route -- */

  private drawRoute(data: ReturnType<typeof save.load>): void {
    const furthest = furthestUnlockedIndex(data);
    // Spline parameter of a station: control point i sits at i/(n-1).
    const travelled = furthest / (CAMPAIGN_NODES.length - 1);

    const g = this.add.graphics();
    const points = this.route.getPoints(ROUTE_SAMPLES);

    // Dark under-stroke first: on a light parchment a bare gold line has no
    // contrast, and on the painted map it would fight the terrain.
    g.lineStyle(11, 0x2a1d10, 0.34);
    g.beginPath();
    g.moveTo(points[0].x, points[0].y);
    for (const p of points.slice(1)) g.lineTo(p.x, p.y);
    g.strokePath();

    // Travelled road: solid gold. Unknown country: dashes dark enough to read
    // as an unfinished track on parchment rather than a rendering artefact.
    for (let i = 0; i < points.length - 1; i++) {
      const t = i / (points.length - 1);
      const a = points[i];
      const b = points[i + 1];
      if (t <= travelled) {
        g.lineStyle(5, THEME.hex.gold, 0.95);
        g.lineBetween(a.x, a.y, b.x, b.y);
      } else if (i % 9 < 5) {
        g.lineStyle(4, 0x6b5228, 0.8);
        g.lineBetween(a.x, a.y, b.x, b.y);
      }
    }

    // Light trail into the next station the player may take.
    if (furthest > 0) this.startTrail(furthest - 1, furthest);
  }

  /** A mote of light running the last stretch of open road, on repeat. */
  private startTrail(fromIndex: number, toIndex: number): void {
    const span = CAMPAIGN_NODES.length - 1;
    const from = fromIndex / span;
    const to = toIndex / span;

    const dot = this.add.circle(0, 0, 5, THEME.hex.gold, 1).setDepth(THEME.depths.units);
    const halo = this.add.circle(0, 0, 11, THEME.hex.gold, 0.28).setDepth(THEME.depths.units - 1);
    this.trail = dot;

    const carrier = { t: from };
    const tween = this.tweens.add({
      targets: carrier,
      t: to,
      duration: 1500,
      repeat: -1,
      repeatDelay: 700,
      ease: "Sine.easeInOut",
      onUpdate: () => {
        const p = this.route.getPoint(carrier.t);
        dot.setPosition(p.x, p.y);
        halo.setPosition(p.x, p.y);
        // Fade in and out at the ends so it reads as a trail, not a bug.
        const edge = Math.min(carrier.t - from, to - carrier.t) / Math.max(0.0001, (to - from) / 2);
        const alpha = Phaser.Math.Clamp(edge, 0, 1);
        dot.setAlpha(alpha);
        halo.setAlpha(alpha * 0.28);
      },
    });
    this.lifecycle.add(() => tween.remove());
  }

  /* ------------------------------------------------------------- stations -- */

  private drawStations(data: ReturnType<typeof save.load>): void {
    const furthest = furthestUnlockedIndex(data);

    CAMPAIGN_MAPS.forEach((map, index) => {
      const node = CAMPAIGN_NODES[index];
      if (!node) return;

      const locked = isMapLocked(map, data);
      const cleared = data.campaign.cleared[map.key] ?? 0;
      // The one station the player is being pointed at: open but not yet beaten.
      const isNext = !locked && cleared === 0 && index === furthest;

      const seal = this.add.container(node.x, node.y).setDepth(THEME.depths.units + 10);
      const rim = locked ? THEME.hex.lock : THEME.hex.gold;
      const body = locked ? 0x3a2c1e : 0x8e1d1d;

      const art = this.add.graphics();
      art.fillStyle(0x000000, 0.34);
      art.fillCircle(2, 4, SEAL_RADIUS + 2);
      art.fillStyle(body, locked ? 0.9 : 1);
      art.fillCircle(0, 0, SEAL_RADIUS);
      art.lineStyle(3, rim, locked ? 0.65 : 1);
      art.strokeCircle(0, 0, SEAL_RADIUS);
      art.lineStyle(1, rim, locked ? 0.3 : 0.55);
      art.strokeCircle(0, 0, SEAL_RADIUS - 6);
      seal.add(art);

      const glyph = this.add.text(0, 0, locked ? "🔒" : String(index + 1), {
        fontFamily: THEME.fonts.display,
        fontSize: locked ? "20px" : "24px",
        color: locked ? THEME.colors.lock : THEME.colors.parchment,
        stroke: THEME.colors.ironDeep,
        strokeThickness: 3,
      }).setOrigin(0.5);
      seal.add(glyph);

      if (!locked) addStars(this, 0, SEAL_RADIUS + 15, cleared, seal, 13, 15);

      // Name plate under the seal, so the map is identifiable without hovering.
      const plate = this.add.text(0, SEAL_RADIUS + (locked ? 14 : 32), map.name.toUpperCase(), {
        fontFamily: THEME.fonts.display,
        fontSize: "13px",
        color: locked ? THEME.colors.lock : THEME.colors.parchment,
        stroke: THEME.colors.ironDeep,
        strokeThickness: 4,
      }).setOrigin(0.5);
      seal.add(plate);

      if (isNext) {
        const pulse = this.add.circle(0, 0, SEAL_RADIUS + 6, THEME.hex.gold, 0).setStrokeStyle(2, THEME.hex.gold, 0.9);
        seal.addAt(pulse, 0);
        const tween = this.tweens.add({
          targets: pulse, scale: 1.35, alpha: 0,
          duration: 1400, repeat: -1, ease: "Sine.easeOut",
        });
        this.lifecycle.add(() => tween.remove());
      }

      const hit = this.add.zone(node.x, node.y, SEAL_RADIUS * 2 + 16, SEAL_RADIUS * 2 + 16)
        .setInteractive({ cursor: locked ? "not-allowed" : "pointer" })
        .setDepth(THEME.depths.units + 20);
      hit.on("pointerover", () => {
        this.tweens.add({ targets: seal, scale: 1.12, duration: 140, ease: "Back.easeOut" });
        this.showPreview(map, node, locked, cleared);
      });
      hit.on("pointerout", () => {
        this.tweens.add({ targets: seal, scale: 1, duration: 130, ease: "Sine.easeIn" });
        this.hidePreview();
      });
      hit.on("pointerdown", () => this.selectMap(map, data, node));

      this.seals[index] = seal;
    });
  }

  /* -------------------------------------------------------------- preview -- */

  /** Hover card: the same painted battlefield the player is about to fight on,
   *  so a station on the world map promises what it delivers. */
  private showPreview(map: MapDef, node: { x: number; y: number }, locked: boolean, cleared: number): void {
    this.hidePreview();

    const cardW = 250;
    const imageH = 141;
    const cardH = imageH + 62;
    // Flip below the seal when the card would run off the top of the screen.
    const above = node.y - SEAL_RADIUS - 18 - cardH > 108;
    const cx = Phaser.Math.Clamp(node.x, cardW / 2 + 16, GAME_WIDTH - cardW / 2 - 16);
    const cy = above ? node.y - SEAL_RADIUS - 18 - cardH / 2 : node.y + SEAL_RADIUS + 46 + cardH / 2;

    const c = this.add.container(cx, cy).setDepth(THEME.depths.hud).setAlpha(0).setScale(0.96);

    const accent = locked ? THEME.hex.blood : THEME.hex.gold;
    const chrome = this.add.graphics();
    chrome.fillStyle(0x000000, 0.45);
    chrome.fillRoundedRect(-cardW / 2 - 5, -cardH / 2 - 3, cardW + 10, cardH + 10, 10);
    chrome.fillStyle(THEME.hex.ironDeep, 0.97);
    chrome.fillRoundedRect(-cardW / 2, -cardH / 2, cardW, cardH, 8);
    chrome.lineStyle(2, accent, 1);
    chrome.strokeRoundedRect(-cardW / 2, -cardH / 2, cardW, cardH, 8);
    c.add(chrome);

    const textureKey = `map-preview-${map.key}`;
    if (this.textures.exists(textureKey)) {
      const shot = this.add.image(0, -cardH / 2 + imageH / 2 + 6, textureKey)
        .setDisplaySize(cardW - 12, imageH);
      if (locked) shot.setTint(0x66707a).setAlpha(0.5);
      c.add(shot);
    }

    c.add(this.add.text(0, cardH / 2 - 44, map.name, {
      fontFamily: THEME.fonts.display, fontSize: "16px",
      color: locked ? THEME.colors.lock : THEME.colors.gold,
    }).setOrigin(0.5));

    c.add(this.add.text(0, cardH / 2 - 20, locked ? lockRequirement(map) : map.description, {
      fontFamily: THEME.fonts.body, fontSize: "12px",
      color: locked ? THEME.colors.blood : THEME.colors.parchment,
      align: "center", wordWrap: { width: cardW - 24 },
    }).setOrigin(0.5));

    if (!locked && cleared > 0) addStars(this, 0, -cardH / 2 + 14, cleared, c, 14, 17);

    this.tweens.add({ targets: c, alpha: 1, scale: 1, duration: 150, ease: "Cubic.easeOut" });
    this.preview = c;
  }

  private hidePreview(): void {
    this.preview?.destroy();
    this.preview = undefined;
  }

  /* ------------------------------------------------------------ selection -- */

  private selectMap(map: MapDef, data: ReturnType<typeof save.load>, node: { x: number; y: number }): void {
    if (this.popup) return;
    if (isMapLocked(map, data)) {
      this.flashLockedHint(node.x, node.y, map);
      return;
    }
    this.hidePreview();
    this.popup = openDifficultyPopup(this, map, {
      cleared: data.campaign.cleared[map.key] ?? 0,
      onClose: () => this.closePopup(),
      onStart: (chosen, difficulty) => {
        this.registry.set("selectedMap", chosen.key);
        this.registry.set("selectedDifficulty", difficulty);
        this.registry.set("runTutorial",
          chosen.key === "greenwold_pass" && difficulty === 1 && !save.get().tutorialCompleted);
        goTo(this, "GameScene", { launch: ["UIScene"] });
      },
    });
  }

  private closePopup(): void {
    this.popup?.destroy();
    this.popup = undefined;
  }

  private flashLockedHint(x: number, y: number, map: MapDef): void {
    const txt = this.add.text(x, y - SEAL_RADIUS - 20, `Locked — ${lockRequirement(map)}`, {
      fontFamily: THEME.fonts.display, fontSize: "16px", color: THEME.colors.blood,
      stroke: THEME.colors.ironDeep, strokeThickness: 4,
    }).setOrigin(0.5).setDepth(THEME.depths.tooltip);
    this.tweens.add({
      targets: txt, alpha: 0, y: y - SEAL_RADIUS - 54,
      duration: 1100, onComplete: () => txt.destroy(),
    });
  }

  /* --------------------------------------------------------------- chrome -- */

  private drawChrome(data: ReturnType<typeof save.load>): void {
    const cx = GAME_WIDTH / 2;

    this.add.text(cx, 40, "CAMPAIGN", {
      fontFamily: THEME.fonts.display, fontSize: "36px", color: THEME.colors.gold,
      stroke: THEME.colors.ironDeep, strokeThickness: 5,
    }).setOrigin(0.5);

    const beaten = CAMPAIGN_MAPS.filter(m => (data.campaign.cleared[m.key] ?? 0) > 0).length;
    this.add.text(cx, 74, `${beaten} of ${CAMPAIGN_MAPS.length} realms held  •  clear a realm to open the next`, {
      fontFamily: THEME.fonts.body, fontSize: "15px", color: THEME.colors.parchmentDim,
      stroke: THEME.colors.ironDeep, strokeThickness: 3,
    }).setOrigin(0.5);

    menuButton(this, 92, 46, 132, 40, "‹ MENU", {
      name: "campaign-back", fontSize: 15, display: true, onClick: () => this.back(),
    });

    menuButton(this, GAME_WIDTH - 116, GAME_HEIGHT - 30, 196, 40,
      `SKILL TREE  •  ${data.skillPoints}`, {
        name: "campaign-skill-tree", fontSize: 14, display: true,
        onClick: () => goTo(this, "SkillTreeScene"),
      });

    this.add.text(28, GAME_HEIGHT - 30, "Hover a seal to scout the realm", {
      fontFamily: THEME.fonts.body, fontSize: "13px", color: THEME.colors.parchmentDim,
    }).setOrigin(0, 0.5);
  }

  private back(): void {
    goTo(this, "MenuScene");
  }
}
